/* Hallmark · component: RackSummaryPanel · genre: modern-minimal
 * states: default · hover · focus-visible · active · disabled
 * contrast: pass (40-41) · tokens: pass (48) · responsive: pass (34, 49)
 * Colors: project CSS tokens only (var(--bg-app), var(--border-color), var(--text-primary)).
 * Layout: single-stream document scroll, no nested scroll trap, Zero-U infrastructure visibility.
 */
import { useState } from 'react';
import {
  Server, MapPin, User, BarChart3, Info,
  Ruler, Building2, Grid3X3, Hash, Plus, Bookmark, Trash2,
  Activity, Tag, Weight, Zap, ChevronDown, Search, X, Thermometer,
} from 'lucide-react';
import type { RackEquipmentItem as Equipment } from '../canvas/rackTypes';
import { rackDisplayTitle } from '../../utils/rackEdit.logic';
import { rackLabel } from '../../utils/rackName.logic';
import type { RackReservation, SpaceMetrics } from '../../utils/rackReservation.logic';
import {
  normalizeRackStatus,
  normalizeRackRole,
  RACK_STATUS_METAS,
  RACK_ROLE_METAS,
} from '../../utils/rackLifecycle.logic';
import { categorizeEquipment, findLargestFreeBlock, filterEquipment } from '../../utils/rackZeroU.logic';
import { formatEquipmentRating, summarizeEquipmentRating } from '../../utils/rackMetrics.logic';

/* ── Types ───────────────────────────────────────────────────────────────── */

interface FreeBlock { start: number; size: number; }

interface RackConfig {
  name?: string;
  label?: string | null;
  type?: string;
  u_slots?: number | null;
  width_m?: number;
  depth_m?: number;
  max_weight_kg?: number | null;
  max_power_w?: number | null;
  tenant?: string;
  contact_email?: string;
  row_index?: number;
  rack_index?: number;
  [key: string]: any;
}

interface SysSettings {
  rack_fill_threshold_warning?: number;
  rack_fill_threshold_critical?: number;
  [key: string]: any;
}

interface RackSummaryPanelProps {
  currentRack: RackConfig | null;
  equipmentList: Equipment[];
  racksList: any[];
  dcInfoName?: string;
  actualCode: string;
  actualRackId: string | number | undefined;
  theme: 'dark' | 'light';
  sysSettings: SysSettings;
  slots: number;
  fillPct: number;
  usedU: number;
  currentWeight: number;
  currentPower: number;
  freeUBlocks: FreeBlock[];
  reservations?: RackReservation[];
  spaceMetrics?: SpaceMetrics;
  onAddEquipment: () => void;
  isRefreshing?: boolean;
  onAddReservation?: () => void;
  onReleaseReservation?: (res: RackReservation) => void;
  onSelectEquipment: (eq: Equipment) => void;
  onSlotClick: (u: number, mount: string) => void;
  onSelectRack?: (rackId: string) => void;
  DcMiniMap: React.ComponentType<{ racksList: any[]; currentRackId: string | number; onSelectRack?: (rackId: string) => void; className?: string }>;
  selectedEqId?: string | number;
  liveMetrics?: Record<string, { temp_c?: number; power_w?: number }> | null;
}

/* ── TYPE_COLORS local (project CSS variables with fallback) ────────── */
const TYPE_COLORS: Record<string, string> = {
  server: 'var(--color-eq-server, #00d2ff)',
  switch: 'var(--color-eq-switch, #9d4edd)',
  storage: 'var(--color-eq-storage, #ff9e00)',
  firewall: 'var(--color-eq-firewall, #ff007f)',
  router: 'var(--color-eq-router, #39ff14)',
  pdu: 'var(--color-eq-pdu, #ff003c)',
  ups: 'var(--color-eq-ups, #ffb703)',
  patch_panel: 'var(--color-eq-patch, #00f5d4)',
};

/* ── Section card wrapper ─────────────────────────────────────────────── */
function SectionCard({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={className}
      style={{
        background: 'var(--bg-app)',
        border: '1px solid var(--border-color)',
        borderRadius: '2px',
        padding: '12px',
      }}
    >
      {children}
    </div>
  );
}

/* ── Section heading ──────────────────────────────────────────────────── */
function SectionHead({
  icon: Icon,
  children,
  action,
}: {
  icon?: React.ElementType;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between mb-2.5">
      <div className="flex items-center gap-1.5">
        {Icon && <Icon size={11} style={{ color: 'var(--accent)' }} aria-hidden />}
        <span
          className="text-[9px] uppercase tracking-[0.12em] font-semibold"
          style={{ color: 'var(--text-muted)' }}
        >
          {children}
        </span>
      </div>
      {action && <div>{action}</div>}
    </div>
  );
}

/* ── KV row ───────────────────────────────────────────────────────────── */
function KVRow({ icon: Icon, label, value, accent, className }: {
  icon?: React.ElementType;
  label: string;
  value: string | React.ReactNode;
  accent?: string;
  className?: string;
}) {
  return (
    <div className={`flex items-center justify-between gap-1.5 py-1 ${className || ''}`} style={{ borderBottom: '1px solid var(--border-color)' }}>
      <span className="flex items-center gap-1 text-[10px] shrink-0" style={{ color: 'var(--text-muted)' }}>
        {Icon && <Icon size={10} aria-hidden />}
        {label}
      </span>
      <span
        className="text-[11px] font-mono tabular-nums text-right min-w-0 [overflow-wrap:anywhere]"
        style={{ color: accent || 'var(--text-secondary)' }}
      >
        {value}
      </span>
    </div>
  );
}

/* ── Space capacity 3-segment progress bar ────────────────────────────── */
function SpaceCapacityBar({ metrics, fillPct }: { metrics?: SpaceMetrics; fillPct: number }) {
  if (!metrics) {
    const color =
      fillPct >= 90 ? 'var(--status-critical)' :
      fillPct >= 70 ? 'var(--status-warning)' :
      'var(--status-normal)';
    return (
      <div
        style={{
          height: 4,
          background: 'var(--bg-input)',
          border: '1px solid var(--border-color)',
          borderRadius: 2,
          overflow: 'hidden',
        }}
        role="progressbar"
        aria-valuenow={fillPct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Заполнение стойки ${fillPct}%`}
      >
        <div style={{ width: `${fillPct}%`, height: '100%', background: color, transition: 'width 500ms ease' }} />
      </div>
    );
  }

  return (
    <div
      style={{
        height: 6,
        background: 'var(--bg-input)',
        border: '1px solid var(--border-color)',
        borderRadius: 2,
        overflow: 'hidden',
        display: 'flex',
      }}
      role="progressbar"
      aria-label={`Занято ${metrics.occupiedPct}%, Резерв ${metrics.reservedPct}%, Свободно ${metrics.availablePct}%`}
    >
      <div
        style={{
          width: `${metrics.occupiedPct}%`,
          height: '100%',
          background: 'var(--color-accent-blue, #38bdf8)',
          transition: 'width 300ms ease',
        }}
        title={`Занято оборудованием: ${metrics.occupiedU}U (${metrics.occupiedPct}%)`}
      />
      <div
        style={{
          width: `${metrics.reservedPct}%`,
          height: '100%',
          background: 'repeating-linear-gradient(45deg, #f59e0b, #f59e0b 3px, #d97706 3px, #d97706 6px)',
          transition: 'width 300ms ease',
        }}
        title={`Зарезервировано: ${metrics.reservedU}U (${metrics.reservedPct}%)`}
      />
      <div
        style={{
          width: `${metrics.availablePct}%`,
          height: '100%',
          background: 'transparent',
        }}
        title={`Свободно: ${metrics.availableU}U (${metrics.availablePct}%)`}
      />
    </div>
  );
}

/* ── Main panel ───────────────────────────────────────────────────────── */
export function RackSummaryPanel({
  currentRack,
  equipmentList,
  racksList,
  dcInfoName,
  actualCode,
  actualRackId,
  sysSettings: _sysSettings,
  slots,
  fillPct,
  usedU,
  currentWeight,
  currentPower,
  freeUBlocks = [],
  reservations = [],
  spaceMetrics,
  onAddEquipment,
  isRefreshing = false,
  onSlotClick,
  onAddReservation,
  onReleaseReservation,
  onSelectEquipment,
  onSelectRack,
  DcMiniMap,
  selectedEqId,
  liveMetrics,
}: RackSummaryPanelProps) {
  const [showMiniMap, setShowMiniMap] = useState(true);
  const [eqSearch, setEqSearch] = useState('');
  const [eqCategory, setEqCategory] = useState<'all' | 'server' | 'network' | 'power' | 'storage'>('all');

  const rackTypeName: Record<string, string> = {
    server_rack:  '19″ Серверная',
    network_rack: '19″ Сетевая',
    open_frame:   'Открытая рама',
    custom_frame: 'Нестандартная рама',
    telecom:      'Телеком шкаф',
    pdu_frame:    'PDU Frame',
  };

  const { mounted: mountedEq, zeroU: zeroUEq } = categorizeEquipment(equipmentList);
  const maxFreeBlock = findLargestFreeBlock(freeUBlocks);
  const filteredMountedEq = filterEquipment(mountedEq, eqSearch, eqCategory);

  // Aggregate live telemetry if available
  let livePowerTotal = 0;
  let hasLivePower = false;
  let livePowerCount = 0;
  let maxTemp: number | null = null;
  let tempSum = 0;
  let tempCount = 0;

  if (liveMetrics) {
    for (const eq of equipmentList) {
      const m = liveMetrics[String(eq.id)];
      if (m?.power_w != null && !isNaN(m.power_w)) {
        livePowerTotal += m.power_w;
        hasLivePower = true;
        livePowerCount++;
      }
      if (m?.temp_c != null && !isNaN(m.temp_c)) {
        if (maxTemp === null || m.temp_c > maxTemp) {
          maxTemp = m.temp_c;
        }
        tempSum += m.temp_c;
        tempCount++;
      }
    }
  }
  const avgTemp = tempCount > 0 ? tempSum / tempCount : null;

  return (
    <div className="flex flex-col gap-3">

      {/* ── Header: rack identity + action ── */}
      <SectionCard>
        {/* Title row */}
        <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
          <div className="min-w-0 flex-1 basis-[160px]">
            <h2 className="text-sm font-bold leading-tight [overflow-wrap:anywhere]" style={{ color: 'var(--text-primary)' }}>
              {currentRack?.row_index != null && currentRack?.rack_index != null
                ? rackLabel({
                    label: currentRack.label,
                    address_confirmed: currentRack.address_confirmed,
                    row_index: currentRack.row_index,
                    rack_index: currentRack.rack_index,
                  })
                : rackDisplayTitle({
                    label: currentRack?.label ?? undefined,
                    id: actualRackId != null ? Number(actualRackId) : undefined,
                  })}
            </h2>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1">
              <span className="flex items-center gap-1 text-[10px]" style={{ color: 'var(--text-muted)' }}>
                <MapPin size={10} aria-hidden />
                {dcInfoName || actualCode.toUpperCase()}
                {currentRack?.address_confirmed === true && currentRack.row_index != null ? ` · Ряд ${currentRack.row_index + 1}` : ''}
                {currentRack?.address_confirmed === true && currentRack.rack_index != null ? ` · Поз. ${currentRack.rack_index + 1}` : ''}
              </span>
              {currentRack?.contact_email && (
                <span className="flex items-center gap-1 text-[10px]" style={{ color: 'var(--text-muted)' }}>
                  <User size={10} aria-hidden />
                  {currentRack.contact_email}
                </span>
              )}
            </div>
            {currentRack?.address_confirmed === false && !currentRack.is_facility && currentRack.type !== 'pillar' && currentRack.type !== 'column' && (
              <div className="mt-2 text-xs leading-relaxed" style={{ color: 'var(--text-primary)' }}>
                <p className="font-semibold">Место не подтверждено</p>
                <p style={{ color: 'var(--text-muted)' }}>Метка не является действующим адресом стойки.</p>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-1.5 max-w-full">
            {onAddReservation && (
              <button
                type="button"
                onClick={onAddReservation}
                disabled={isRefreshing || currentRack?.u_slots == null || slots <= 0 || currentRack?.is_blocked}
                className="min-h-11 flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold whitespace-nowrap rounded-sm border transition-colors border-amber-500/50 text-amber-400 hover:bg-amber-500/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)] disabled:opacity-50 disabled:cursor-not-allowed"
                title="Зарезервировать юниты под проект / клиента"
              >
                <Bookmark size={11} />
                Резерв
              </button>
            )}

            <button
              onClick={onAddEquipment}
              disabled={isRefreshing || currentRack?.u_slots == null || slots <= 0 || currentRack?.is_blocked}
              title={currentRack?.u_slots == null ? 'Укажите вместимость U в параметрах стойки' : undefined}
              className="min-h-11 flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold whitespace-nowrap rounded-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)] disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: 'var(--accent)', color: '#fff' }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--accent-hover)' }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--accent)' }}
            >
              <Plus size={12} aria-hidden />
              Монтировать
            </button>
          </div>
        </div>

        {/* Unified Space Utilization & Capacity Widget */}
        <div className="mt-3 pt-3 border-t" style={{ borderColor: 'var(--border-color)' }}>
          {currentRack?.u_slots == null ? <p className="text-sm text-text-secondary">Вместимость U и свободное место неизвестны.</p> : slots <= 0 ? <p className="text-sm text-text-secondary">Монтажное пространство отсутствует (0U).</p> : <>
          <div className="flex items-center justify-between mb-1.5">
            <span className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.1em] font-semibold" style={{ color: 'var(--text-muted)' }}>
              <BarChart3 size={11} aria-hidden />
              Утилизация юнитов ({slots}U)
            </span>
            <span
              className="text-[11px] font-bold font-mono tabular-nums"
              style={{
                color: fillPct >= 90 ? 'var(--status-critical)' :
                       fillPct >= 70 ? 'var(--status-warning)' :
                       'var(--accent)',
              }}
            >
              {spaceMetrics ? spaceMetrics.utilizationPct : fillPct}% {spaceMetrics?.reservedU ? 'занято и в резерве' : 'занято'}
            </span>
          </div>

          <SpaceCapacityBar metrics={spaceMetrics} fillPct={fillPct} />

          {/* Compact 3-way breakdown */}
          <div className="flex items-center justify-between gap-2 mt-2 text-[10px] font-mono tabular-nums">
            <div className="flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ background: 'var(--color-accent-blue, #38bdf8)' }} />
              <span style={{ color: 'var(--text-primary)' }}>
                Занято: <strong>{spaceMetrics ? spaceMetrics.occupiedU : usedU}U</strong>
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ background: '#f59e0b' }} />
              <span style={{ color: 'var(--text-primary)' }}>
                Бронь: <strong>{spaceMetrics ? spaceMetrics.reservedU : 0}U</strong>
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full shrink-0 border" style={{ borderColor: 'var(--border-color)', background: 'transparent' }} />
              <span style={{ color: 'var(--text-primary)' }}>
                Свободно: <strong>{spaceMetrics ? spaceMetrics.availableU : slots - usedU}U</strong>
              </span>
            </div>
          </div>

          {/* Largest continuous block highlight */}
          {maxFreeBlock && maxFreeBlock.size > 0 && (
            <div
              className="mt-2 px-2 py-1 rounded-sm text-[10px] flex items-center justify-between"
              style={{
                background: 'var(--bg-panel)',
                border: '1px solid var(--border-color)',
                color: 'var(--text-muted)',
              }}
            >
              <span>Непрерывный свободный блок:</span>
              <span className="font-mono font-bold" style={{ color: 'var(--accent)' }}>
                U{maxFreeBlock.start}–U{maxFreeBlock.start + maxFreeBlock.size - 1} ({maxFreeBlock.size}U подряд)
              </span>
            </div>
          )}
          </>}
        </div>
      </SectionCard>

      {/* ── NetBox Slot Reservations List ── */}
      {reservations && reservations.length > 0 && (
        <SectionCard>
          <div className="flex items-center justify-between mb-2">
            <SectionHead icon={Bookmark}>Активные резервы ({reservations.length})</SectionHead>
          </div>
          <div className="flex flex-col gap-1.5">
            {reservations.map(res => {
              const range = res.u_height > 1
                ? `U${res.u_start}..U${res.u_start + res.u_height - 1}`
                : `U${res.u_start}`;
              return (
                <div
                  key={res.id}
                  className="flex items-center justify-between p-1.5 rounded border border-amber-500/30 bg-amber-500/5 text-[11px] font-mono"
                >
                  <div className="min-w-0 flex-1 truncate pr-2">
                    <div className="flex items-center gap-1.5">
                      <span className="px-1 py-0.2 rounded text-[8px] font-bold bg-amber-500/20 text-amber-300 border border-amber-500/40">
                        {range} ({res.u_height}U)
                      </span>
                      <span className="font-bold text-amber-300 truncate">
                        {res.tenant}
                      </span>
                    </div>
                    {res.description && (
                      <p className="text-[9px] truncate mt-0.5" style={{ color: 'var(--text-muted)' }}>
                        {res.description}
                      </p>
                    )}
                  </div>

                  {onReleaseReservation && (
                    <button
                      type="button"
                      onClick={() => onReleaseReservation(res)}
                      title="Освободить зарезервированные юниты"
                      className="p-1 rounded transition-colors shrink-0"
                      style={{ color: 'var(--text-muted)' }}
                      onMouseEnter={e => {
                        (e.currentTarget as HTMLElement).style.color = '#ef4444';
                        (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
                      }}
                      onMouseLeave={e => {
                        (e.currentTarget as HTMLElement).style.color = 'var(--text-muted)';
                        (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                      }}
                      aria-label="Освободить резерв"
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </SectionCard>
      )}

      {/* ── Passport section ── */}
      <SectionCard>
        <SectionHead icon={Info}>Паспорт стойки</SectionHead>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5">
          <KVRow
            icon={Grid3X3}
            label="Тип"
            value={rackTypeName[currentRack?.type || ''] || currentRack?.type || 'Стандартная'}
          />
          {(() => {
            const status = normalizeRackStatus(currentRack?.status);
            const statusMeta = RACK_STATUS_METAS[status];
            return (
              <KVRow
                icon={Activity}
                label="Статус"
                value={
                  <span className="flex items-center gap-1.5">
                    <span
                      className="inline-block w-2 h-2 rounded-full shrink-0"
                      style={{ background: statusMeta.color }}
                    />
                    <span className="truncate">{statusMeta.labelRu}</span>
                  </span>
                }
                accent={statusMeta.color}
              />
            );
          })()}
          {(() => {
            const role = normalizeRackRole(currentRack?.role, currentRack?.type);
            const roleMeta = RACK_ROLE_METAS[role];
            return (
              <KVRow
                icon={Tag}
                label="Роль"
                value={`${roleMeta.labelRu} [${roleMeta.shortCode}]`}
                accent={roleMeta.color}
              />
            );
          })()}
          <KVRow
            icon={MapPin}
            label="Площадка"
            value={dcInfoName || currentRack?.datacenter_code || actualCode}
          />
          <KVRow
            icon={Ruler}
            label="U / основание"
            value={`${currentRack?.u_slots == null ? 'U неизвестно' : `${slots}U`} · ${currentRack?.width_m ? `${(currentRack.width_m * 100).toFixed(0)}см` : 'ширина неизвестна'} × ${currentRack?.depth_m ? `${(currentRack.depth_m * 100).toFixed(0)}см` : 'глубина неизвестна'}`}
          />
          <KVRow
            icon={Weight}
            label="Вес"
            value={currentRack?.max_weight_kg != null && !summarizeEquipmentRating(equipmentList, 'weight_kg').unknownCount
              ? `${currentWeight} / ${currentRack.max_weight_kg} кг`
              : `${formatEquipmentRating(equipmentList, 'weight_kg', 'кг')} · ${currentRack?.max_weight_kg == null ? 'лимит неизвестен' : `лимит ${currentRack.max_weight_kg} кг`}`}
            accent={currentRack?.max_weight_kg && currentWeight > currentRack.max_weight_kg ? 'var(--status-critical)' : undefined}
          />
          <KVRow
            icon={Zap}
            label="Мощность"
            value={currentRack?.max_power_w != null && !summarizeEquipmentRating(equipmentList, 'power_w').unknownCount
                ? `${currentPower} / ${currentRack.max_power_w} Вт`
                : `${formatEquipmentRating(equipmentList, 'power_w', 'Вт')} · ${currentRack?.max_power_w == null ? 'лимит неизвестен' : `лимит ${currentRack.max_power_w} Вт`}`
            }
            accent={
              (hasLivePower && currentRack?.max_power_w && livePowerTotal > currentRack.max_power_w * 0.8) ||
              (currentRack?.max_power_w && currentPower > currentRack.max_power_w * 0.8)
                ? 'var(--status-warning)'
                : undefined
            }
          />
          {hasLivePower && <KVRow icon={Zap} label="Показания мощности"
            value={`Получено ${Math.round(livePowerTotal)} Вт · показания: ${livePowerCount} из ${equipmentList.length}. Давность измерения неизвестна.`} />}
          {maxTemp !== null && (
            <KVRow
              icon={Thermometer}
              label="Температура"
              value={avgTemp !== null && Math.abs(avgTemp - maxTemp) > 1
                ? `${avgTemp.toFixed(1)}°C (пик ${maxTemp.toFixed(1)}°C)`
                : `${maxTemp.toFixed(1)}°C`
              }
              accent={
                maxTemp > 32 ? 'var(--status-critical)' :
                maxTemp > 27 ? 'var(--status-warning)' :
                'var(--color-success, #10b981)'
              }
            />
          )}
          <KVRow icon={Building2} label="Тенант" value={currentRack?.tenant || 'mine'} />
          {currentRack?.netbox_id != null && (
            <KVRow icon={Hash} label="NetBox ID" value={String(currentRack.netbox_id)} />
          )}
        </div>
      </SectionCard>

      {/* ── Equipment list ── */}
      <SectionCard>
        <SectionHead
          icon={Server}
          action={
            mountedEq.length > 0 && (
              <span className="text-[10px] font-mono font-bold" style={{ color: 'var(--accent)' }}>
                {filteredMountedEq.length} / {mountedEq.length}
              </span>
            )
          }
        >
          Оборудование ({mountedEq.length})
        </SectionHead>

        {mountedEq.length > 5 && (
          <div className="flex flex-col gap-2 mb-2.5">
            <div className="relative flex items-center">
              <Search size={12} className="absolute left-2 pointer-events-none" style={{ color: 'var(--text-muted)' }} />
              <input
                type="text"
                value={eqSearch}
                onChange={e => setEqSearch(e.target.value)}
                placeholder="Поиск по имени, U, модели..."
                className="w-full pl-6 pr-6 py-1 text-[11px] rounded-sm transition-colors focus:outline-none focus:ring-1"
                style={{
                  background: 'var(--bg-input)',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-primary)',
                  ['--tw-ring-color' as string]: 'var(--border-focus)',
                }}
              />
              {eqSearch && (
                <button
                  type="button"
                  onClick={() => setEqSearch('')}
                  className="absolute right-1.5 p-0.5 rounded transition-colors"
                  style={{ color: 'var(--text-muted)' }}
                  title="Очистить поиск"
                >
                  <X size={12} />
                </button>
              )}
            </div>

            <div className="flex items-center gap-1 flex-wrap text-[10px]">
              {[
                { id: 'all', label: 'Все' },
                { id: 'server', label: 'Серверы' },
                { id: 'network', label: 'Сеть' },
                { id: 'storage', label: 'Storage' },
              ].map(cat => {
                const isActive = eqCategory === cat.id;
                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setEqCategory(cat.id as any)}
                    className="px-2 py-0.5 rounded-sm font-semibold transition-colors"
                    style={{
                      background: isActive ? 'var(--bg-active)' : 'transparent',
                      color: isActive ? 'var(--accent)' : 'var(--text-muted)',
                      border: isActive ? '1px solid var(--border-focus)' : '1px solid var(--border-color)',
                    }}
                  >
                    {cat.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-1">
          {mountedEq.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                {currentRack?.u_slots == null ? 'Горизонтальное оборудование не установлено. Укажите вместимость U для монтажа.'
                  : slots <= 0 ? 'Горизонтальный монтаж недоступен: 0U.' : 'Стойка пуста. Нажмите «Монтировать».'}
              </p>
              <button
                type="button"
                onClick={onAddEquipment}
                disabled={isRefreshing || currentRack?.u_slots == null || slots <= 0 || currentRack?.is_blocked}
                className="mt-2 min-h-11 px-3 py-1 text-[11px] font-semibold rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: 'var(--accent)',
                  color: 'var(--text-on-accent, #ffffff)',
                }}
              >
                + Монтировать оборудование
              </button>
            </div>
          ) : filteredMountedEq.length === 0 ? (
            <div className="text-center py-4 text-[11px]" style={{ color: 'var(--text-muted)' }}>
              <p>Ничего не найдено по фильтру</p>
              <button
                type="button"
                onClick={() => { setEqSearch(''); setEqCategory('all'); }}
                className="mt-1 text-[10px] underline font-semibold"
                style={{ color: 'var(--accent)' }}
              >
                Сбросить фильтр
              </button>
            </div>
          ) : (
            filteredMountedEq
              .sort((a, b) => b.u_position - a.u_position)
              .map(eq => {
                const isSelected = selectedEqId != null && String(eq.id) === String(selectedEqId);
                return (
                  <button
                    key={eq.id}
                    onClick={() => onSelectEquipment(eq)}
                    className="w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-sm transition-colors focus-visible:outline-none focus-visible:ring-1 group"
                    style={{
                      background: isSelected ? 'var(--bg-active)' : 'transparent',
                      border: isSelected ? '1px solid var(--border-focus)' : '1px solid var(--border-color)',
                      ['--tw-ring-color' as string]: 'var(--border-focus)',
                    }}
                    onMouseEnter={e => {
                      if (!isSelected) {
                        (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--border-focus)';
                        (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-hover)';
                      }
                    }}
                    onMouseLeave={e => {
                      if (!isSelected) {
                        (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--border-color)';
                        (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                      }
                    }}
                  >
                    {/* Type color bar */}
                    <div
                      className="w-1 h-4 rounded-full shrink-0"
                      style={{ background: TYPE_COLORS[eq.equipment_type] || 'var(--text-muted)' }}
                      aria-hidden
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <p className="text-[11px] font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                          {eq.name}
                        </p>
                        {isSelected && (
                          <span
                            className="text-[8px] font-bold uppercase px-1 py-0.2 rounded-sm shrink-0"
                            style={{ background: 'var(--accent)', color: 'var(--text-on-accent, #ffffff)' }}
                          >
                            Активен
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap mt-0.5 text-[9px]" style={{ color: 'var(--text-muted)' }}>
                        <span>
                          U{eq.u_position}–U{eq.u_position + eq.u_height - 1}
                          {eq.power_w   ? ` · ${eq.power_w}W`   : ''}
                          {eq.weight_kg ? ` · ${eq.weight_kg}kg` : ''}
                        </span>
                        {liveMetrics?.[String(eq.id)]?.temp_c != null && (
                          <span
                            className="font-mono font-bold px-1 rounded-sm text-[8px]"
                            style={{
                              background: (liveMetrics[String(eq.id)].temp_c! > 32)
                                ? 'rgba(239,68,68,0.2)'
                                : (liveMetrics[String(eq.id)].temp_c! > 27)
                                ? 'rgba(245,158,11,0.2)'
                                : 'rgba(16,185,129,0.2)',
                              color: (liveMetrics[String(eq.id)].temp_c! > 32)
                                ? 'var(--status-critical)'
                                : (liveMetrics[String(eq.id)].temp_c! > 27)
                                ? 'var(--status-warning)'
                                : 'var(--color-success, #10b981)',
                            }}
                          >
                            {liveMetrics[String(eq.id)].temp_c!.toFixed(1)}°C
                          </span>
                        )}
                        {liveMetrics?.[String(eq.id)]?.power_w != null && (
                          <span className="font-mono font-bold text-[8px]" style={{ color: '#f59e0b' }}>
                            ⚡{Math.round(liveMetrics[String(eq.id)].power_w!)}W
                          </span>
                        )}
                      </div>
                    </div>
                    <span
                      className="text-[9px] font-mono shrink-0 px-1.5 py-0.5 rounded-sm"
                      style={{
                        background: isSelected ? 'var(--bg-panel)' : 'var(--bg-input)',
                        color: isSelected ? 'var(--accent)' : 'var(--text-muted)',
                        border: '1px solid var(--border-color)',
                      }}
                    >
                      {eq.u_height}U
                    </span>
                  </button>
                );
              })
          )}
        </div>
      </SectionCard>

      {/* ── Zero-U Infrastructure & lateral PDUs ── */}
      <SectionCard>
        <div className="flex items-center justify-between mb-2">
          <SectionHead icon={Zap}>
            Инфраструктура Zero-U ({zeroUEq.length})
          </SectionHead>
            <button
              type="button"
              onClick={() => onSlotClick(0, 'Left')}
              disabled={isRefreshing || currentRack?.is_blocked || currentRack?.is_facility || currentRack?.u_slots === 0}
              className="text-[9px] font-semibold px-1.5 py-0.5 rounded transition-colors"
              style={{
                background: 'var(--bg-active)',
                color: 'var(--accent)',
                border: '1px solid var(--border-focus)',
              }}
              title="Добавить Zero-U оборудование"
            >
              + PDU
            </button>
        </div>

        <div className="flex flex-col gap-1">
          {zeroUEq.length === 0 ? (
            <div
              className="p-2.5 rounded-sm border border-dashed text-center"
              style={{
                borderColor: 'var(--border-color)',
                background: 'transparent',
              }}
            >
              <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                Вертикальные PDU не смонтированы
              </p>
              <button
                type="button"
                onClick={() => onSlotClick(0, 'Left')}
                disabled={isRefreshing || currentRack?.is_blocked || currentRack?.is_facility || currentRack?.u_slots === 0}
                className="mt-1.5 px-2 py-0.5 text-[10px] font-semibold rounded-sm transition-colors"
                style={{
                  background: 'var(--bg-active)',
                  color: 'var(--accent)',
                  border: '1px solid var(--border-focus)',
                }}
              >
                + Добавить Zero-U PDU
              </button>
            </div>
          ) : (
            zeroUEq.map(eq => {
              const rawMount = String(eq.mounting_position || '').toLowerCase();
              const mountSide = rawMount.includes('left')
                ? 'Левый борт (Ввод A)'
                : rawMount.includes('right')
                ? 'Правый борт (Ввод B)'
                : 'Zero-U канал';
              const feedName = eq.attributes?.feed_name || (mountSide.includes('A') ? 'Feed A' : mountSide.includes('B') ? 'Feed B' : '');
              const isSelected = selectedEqId != null && String(eq.id) === String(selectedEqId);

              return (
                <button
                  key={eq.id}
                  onClick={() => onSelectEquipment(eq)}
                  className="w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-sm transition-colors focus-visible:outline-none focus-visible:ring-1 group"
                  style={{
                    background: isSelected ? 'var(--bg-active)' : 'transparent',
                    border: isSelected ? '1px solid var(--border-focus)' : '1px solid var(--border-color)',
                    ['--tw-ring-color' as string]: 'var(--border-focus)',
                  }}
                  onMouseEnter={e => {
                    if (!isSelected) {
                      (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--border-focus)';
                      (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-hover)';
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isSelected) {
                      (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--border-color)';
                      (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                    }
                  }}
                >
                  <div
                    className="w-1.5 h-4 rounded-full shrink-0"
                    style={{ background: 'var(--color-eq-pdu, #ff003c)' }}
                    aria-hidden
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <p className="text-[11px] font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                        {eq.name}
                      </p>
                      {isSelected && (
                        <span
                          className="text-[8px] font-bold uppercase px-1 py-0.2 rounded-sm shrink-0"
                          style={{ background: 'var(--accent)', color: 'var(--text-on-accent, #ffffff)' }}
                        >
                          Активен
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap mt-0.5 text-[9px]" style={{ color: 'var(--text-muted)' }}>
                      <span>
                        {mountSide}
                        {feedName ? ` · ${feedName}` : ''}
                        {eq.power_w ? ` · ${eq.power_w}W` : ''}
                        {eq.weight_kg ? ` · ${eq.weight_kg}kg` : ''}
                      </span>
                      {liveMetrics?.[String(eq.id)]?.power_w != null && (
                        <span className="font-mono font-bold text-[8px]" style={{ color: '#f59e0b' }}>
                          ⚡{Math.round(liveMetrics[String(eq.id)].power_w!)}W факт
                        </span>
                      )}
                    </div>
                  </div>
                  <span
                    className="text-[9px] font-mono shrink-0 px-1.5 py-0.5 rounded-sm"
                    style={{
                      background: isSelected ? 'var(--bg-panel)' : 'var(--bg-input)',
                      color: isSelected ? 'var(--accent)' : 'var(--accent)',
                      border: '1px solid var(--border-color)',
                    }}
                  >
                    0U
                  </span>
                </button>
              );
            })
          )}
        </div>
      </SectionCard>

      {/* ── Mini-map ── */}
      {racksList && racksList.length > 0 && (
        <SectionCard>
          <SectionHead
            icon={MapPin}
            action={
              <button
                type="button"
                onClick={() => setShowMiniMap(prev => !prev)}
                className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded transition-colors"
                style={{
                  color: 'var(--text-muted)',
                }}
                title={showMiniMap ? 'Свернуть миникарту' : 'Развернуть миникарту'}
                aria-label={showMiniMap ? 'Свернуть миникарту' : 'Развернуть миникарту'}
              >
                <span>{showMiniMap ? 'Свернуть' : 'Развернуть'}</span>
                <ChevronDown
                  size={12}
                  className={`transition-transform duration-200 ${showMiniMap ? '' : '-rotate-90'}`}
                />
              </button>
            }
          >
            Позиция в ЦОД
          </SectionHead>
          {showMiniMap && (
            <DcMiniMap
              racksList={racksList}
              currentRackId={actualRackId != null ? String(actualRackId) : ''}
              onSelectRack={onSelectRack}
            />
          )}
        </SectionCard>
      )}

    </div>
  );
}
